import { useState, type CSSProperties } from 'react';

import type { PrepaidAttachment } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  useDeletePrepaidAttachment,
  usePrepaidAttachmentDownloadUrl,
  usePrepaidAttachments,
  useUploadPrepaidAttachment,
} from '../../lib/queries';
import { Can } from '../Can';

// ============================================================
// PrepaidAttachmentsPanel — "เอกสารหลักฐาน" section for the prepaid detail
// drawer (P7 §9.5). Lists evidence (image thumbnails via short-lived presigned
// URLs, documents as a download link) + an upload control + per-row delete.
// Read/download are open; upload + delete are <Can permission="prepaid.attachment
// .manage"> gated (UX-only — the server enforces with @RequirePermission). Delete
// is soft + audited server-side. Router-free + self-contained so it is unit-
// testable without a router. Mirrors LvaAttachmentsPanel.
// ============================================================

const SECTION_LABEL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 8,
  letterSpacing: 0.2,
};
const SECTION_BAR: CSSProperties = {
  width: 3,
  height: 14,
  background: 'var(--brand)',
  borderRadius: 2,
};
const EMPTY_HINT: CSSProperties = {
  fontSize: 12,
  padding: 14,
  textAlign: 'center',
  border: '1px dashed var(--border)',
  borderRadius: 8,
};

export interface PrepaidAttachmentsPanelProps {
  companyId: string | undefined;
  contractId: string;
  /** When true (company opted in + pre-first-run), show the amber "evidence required" hint. */
  requiresEvidence?: boolean;
}

export function PrepaidAttachmentsPanel({
  companyId,
  contractId,
  requiresEvidence = false,
}: PrepaidAttachmentsPanelProps) {
  const { data: attachments = [] } = usePrepaidAttachments(companyId, contractId);
  const uploadMut = useUploadPrepaidAttachment(companyId, contractId);
  const deleteMut = useDeletePrepaidAttachment(companyId, contractId);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const images = attachments.filter((a) => a.kind === 'IMAGE');
  const documents = attachments.filter((a) => a.kind === 'DOCUMENT');
  const blocking = requiresEvidence && attachments.length === 0;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadErr(null);
    // Each file is attempted independently; collect ALL failures so a failure on
    // file #1 isn't silently overwritten by a later one (only the last error
    // would otherwise survive in the single error slot).
    const failures: string[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 10 * 1024 * 1024) {
        failures.push(`"${f.name}" ใหญ่เกิน 10MB`);
        continue;
      }
      try {
        await uploadMut.mutateAsync({
          file: f,
          kind: f.type.startsWith('image/') ? 'IMAGE' : 'DOCUMENT',
        });
      } catch (e: unknown) {
        failures.push(e instanceof Error ? `"${f.name}": ${e.message}` : `"${f.name}" ไม่สำเร็จ`);
      }
    }
    setUploadErr(failures.length > 0 ? `อัปโหลดไม่สำเร็จ — ${failures.join(' · ')}` : null);
  }

  return (
    <div>
      <div style={SECTION_LABEL}>
        <span style={SECTION_BAR} />
        เอกสารหลักฐาน ({attachments.length})
      </div>

      {blocking && (
        <div
          role="status"
          style={{
            marginBottom: 10,
            background: 'rgba(180,120,10,0.10)',
            color: 'var(--warning,#b4780a)',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          ⚠ ต้องแนบเอกสารก่อนตัดจ่ายงวดแรก — สัญญานี้จะถูกข้ามในรอบตัดจ่ายจนกว่าจะมีเอกสารแนบ
        </div>
      )}

      <Can permission="prepaid.attachment.manage">
        <label
          htmlFor="prepaid-attach-input"
          className="btn sm"
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="upload" size={12} />
          {uploadMut.isPending ? 'กำลังอัปโหลด…' : 'แนบเอกสาร / รูป'}
          <input
            id="prepaid-attach-input"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
        </label>
      </Can>

      {uploadErr && (
        <div
          style={{
            marginTop: 8,
            background: 'rgba(180,35,24,0.08)',
            color: 'var(--negative,#b42318)',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {uploadErr}
        </div>
      )}

      {attachments.length === 0 ? (
        <div className="muted" style={{ ...EMPTY_HINT, marginTop: 10 }}>
          ยังไม่มีเอกสารแนบ — แนบสัญญา / กรมธรรม์ / ใบกำกับภาษี / เอกสารต่ออายุได้
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
          {images.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                gap: 8,
              }}
            >
              {images.map((a) => (
                <AttachmentThumb
                  key={a.id}
                  companyId={companyId}
                  contractId={contractId}
                  attachment={a}
                  onDelete={() => deleteMut.mutate(a.id)}
                />
              ))}
            </div>
          )}
          {documents.length > 0 && (
            <div style={{ display: 'grid', gap: 6 }}>
              {documents.map((a) => (
                <AttachmentRow
                  key={a.id}
                  companyId={companyId}
                  contractId={contractId}
                  attachment={a}
                  onDelete={() => deleteMut.mutate(a.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Flattens the presigned-URL hook into a plain string for the visual rows.
function usePresignedUrl(
  companyId: string | undefined,
  contractId: string,
  attachmentId: string,
): string | null {
  const { data } = usePrepaidAttachmentDownloadUrl(companyId, contractId, attachmentId, true);
  return data?.url ?? null;
}

// AttachmentThumb — image tile with delete on hover.
function AttachmentThumb({
  companyId,
  contractId,
  attachment,
  onDelete,
}: {
  companyId: string | undefined;
  contractId: string;
  attachment: PrepaidAttachment;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const url = usePresignedUrl(companyId, contractId, attachment.id);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#f0f0f3',
        border: '1px solid var(--border)',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={attachment.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-muted)',
            fontSize: 11,
          }}
        >
          กำลังโหลด…
        </div>
      )}
      {hover && (
        <Can permission="prepaid.attachment.manage">
          <button
            type="button"
            onClick={onDelete}
            title="ลบไฟล์"
            aria-label={`ลบ ${attachment.filename}`}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(180,35,24,0.9)',
              color: '#fff',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="x" size={11} />
          </button>
        </Can>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '4px 6px',
          background: 'rgba(0,0,0,0.55)',
          color: '#fff',
          fontSize: 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={attachment.filename}
      >
        {attachment.filename}
      </div>
    </div>
  );
}

// AttachmentRow — document list row with download + delete.
function AttachmentRow({
  companyId,
  contractId,
  attachment,
  onDelete,
}: {
  companyId: string | undefined;
  contractId: string;
  attachment: PrepaidAttachment;
  onDelete: () => void;
}) {
  const url = usePresignedUrl(companyId, contractId, attachment.id);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      <Icon name="download" size={14} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={attachment.filename}
        >
          {attachment.filename}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>
          {(attachment.sizeBytes / 1024).toFixed(1)} KB · {attachment.mimeType}
        </div>
      </div>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn sm"
          style={{ textDecoration: 'none' }}
        >
          เปิด
        </a>
      )}
      <Can permission="prepaid.attachment.manage">
        <button
          type="button"
          className="btn sm"
          onClick={onDelete}
          title="ลบไฟล์"
          aria-label={`ลบ ${attachment.filename}`}
        >
          <Icon name="x" size={11} />
        </button>
      </Can>
    </div>
  );
}
